import React, { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box, Typography, Stack, CircularProgress, Alert, Button, IconButton,
  Table, TableBody, TableCell, TableHead, TableRow, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Select, MenuItem, FormControl, InputLabel, FormControlLabel, FormHelperText, Checkbox, Switch,
  InputAdornment, Popover, Divider,
  InputBase,
} from '@mui/material';
import MenuBookRounded from '@mui/icons-material/MenuBookRounded';
import AddRounded from '@mui/icons-material/AddRounded';
import EditRounded from '@mui/icons-material/EditRounded';
import DeleteOutlineRounded from '@mui/icons-material/DeleteOutlineRounded';
import SearchRounded from '@mui/icons-material/SearchRounded';
import CloseRounded from '@mui/icons-material/CloseRounded';
import { HexColorPicker } from 'react-colorful';

import {
  listInkSeries, upsertInkSeries, deleteInkSeries,
  listInkProducts, upsertInkProduct, deleteInkProduct, getArtworkColours,
  listInkSuppliers,
  InkSeries, InkSeriesUpsert, InkProduct, InkProductUpsert,
} from './productionApi';
import { productionKeys } from './queryKeys';
import { TOKENS } from './board/ganttConfig';

const LMQ_RED = '#d32f2f';
// Coatings live IN the series library as coating-class series (COCO/COUV/COLMQ,
// migration 108). A coating's key is its supplier article number, not colour×series.
const COATING_HEX = '#8C9BA5';   // uniform varnish tint — coatings have no CMYK colour
/** The ink systems, each with the word it stands for — a bare "CO" told a new
 *  user nothing (Jack, 11/09). NEVER sum stock or demand across these. */
const SYSTEMS: { code: string; label: string }[] = [
  { code: 'CO', label: 'Conventional' },
  { code: 'UV', label: 'UV-cure' },
];
const systemLabel = (code: string) =>
  SYSTEMS.find((x) => x.code === code)?.label ?? code;

/**
 * The series key is COMPOSED, never free-typed: supplier prefix + a
 * discriminator for the series + the system + LMQ when it's low migration.
 *
 *   HUBER  ECO       CO        -> H  + E + CO         = HECO
 *   FLINT  ARROWCURE UV        -> F  + A + UV         = FAUV
 *   FLINT  NOVASPOT  CO        -> F  + P + CO         = FPCO
 *   FLINT  NOVASENS  CO  (LMQ) -> F  + E + CO + LMQ   = FECOLMQ
 *   TOKA   161       UV        -> TO +   + UV         = TOUV
 *
 * Only the first two segments are judgement. The supplier prefix is usually the
 * initial but TOKA and COATINGS take two letters, and the discriminator is
 * frankly arbitrary — Jack picked P for NOVASPOT and E for NOVASENS precisely
 * BECAUSE the obvious N would have collided (both would have been FNCO). So the
 * dialog suggests, shows the key assembling segment by segment, and — the part
 * that actually matters — says so when the finished key already exists.
 */
function composeSeriesKey(parts: {
  prefix: string; letter: string; system: string; lowMigration: boolean;
}): string {
  return [parts.prefix, parts.letter, parts.system, parts.lowMigration ? 'LMQ' : '']
    .map((x) => (x || '').trim().toUpperCase())
    .join('');
}

/** First letter of the supplier code — right for HUBER and FLINT, overridable
 *  for the two-letter ones. */
const suggestPrefix = (supplierCode: string) => (supplierCode || '').trim().toUpperCase().slice(0, 1);

/** First letter of the series name, ignoring a leading house word like NOVA
 *  that every series in the range shares and so can't discriminate between. */
const SHARED_RANGE_PREFIXES = ['NOVA'];
function suggestLetter(seriesName: string): string {
  let n = (seriesName || '').trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
  for (const shared of SHARED_RANGE_PREFIXES) {
    if (n.length > shared.length && n.startsWith(shared)) { n = n.slice(shared.length); break; }
  }
  return n.slice(0, 1);
}
const COLOUR_KINDS = ['process', 'pantone', 'special'];

const isCoatingSeries = (s?: { material_class?: string }) => s?.material_class === 'coating';

/** Everything is stored ALL CAPS — force it on entry regardless of caps lock. */
const up = (s: string) => s.toUpperCase();
/** Hexes too: #B80021, never #b80021 (the picker emits lowercase). */
const upHex = (s: string | null | undefined) => (s ?? '').trim().toUpperCase();

/** kg and $/kg are shown to ONE decimal place across the ink side — inputs may
 * accept more, but what we display (and prefill) rounds. */
// Kilograms are shown to 1 dp (Jack, 2026-09-09: "no further decimals than 1"
// across the ink side). MONEY is not rounded — a price of 16.59 is a real
// number off a supplier's invoice and must survive an edit untouched.
const dp1 = (v: number | null | undefined) => (v == null ? '' : String(Math.round(v * 10) / 10));
const asIs = (v: number | null | undefined) => (v == null ? '' : String(v));

/** Mirror of the backend key/description composition — for the live preview.
 * LMQ is NOT appended here: it lives in the series name + suffix already.
 * Coatings are keyed by their article number directly (no colour×series). */
function composeInk(series: InkSeries | undefined, colourCode: string, colourName: string) {
  if (!series || !colourCode.trim()) return { key: '', description: '' };
  if (isCoatingSeries(series)) {
    return { key: colourCode.trim().toUpperCase(), description: colourName.trim().toUpperCase() };
  }
  const key = `${colourCode.trim().toUpperCase()}${series.series_suffix}`;
  const description = [series.manufacturer, colourName.trim().toUpperCase(), series.series_name, series.ink_system]
    .filter(Boolean).join(' ');
  return { key, description };
}

/** Series colour-code chip — the display_hex tag used across the app. Coating
 * series drop the (doubled) system so they read cleanly, e.g. "COATINGS LMQ". */
function SeriesTag({ series, coating }: {
  series: { manufacturer?: string; series_name?: string; ink_system?: string; series_hex?: string | null };
  coating?: boolean;
}) {
  const parts = coating
    ? [series.manufacturer, series.series_name]
    : [series.manufacturer, series.series_name, series.ink_system];
  const label = parts.filter(Boolean).join(' ').toUpperCase();
  const hex = series.series_hex ?? '#999';
  return (
    <Box component="span" sx={{
      display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 0.75, py: '1px',
      borderRadius: '5px', fontSize: 11.5, fontWeight: 600,
      border: `1px solid ${hex}`, borderLeft: `4px solid ${hex}`,
      bgcolor: `${hex}14`, color: TOKENS.ink,
    }}>
      {label || '—'}
    </Box>
  );
}

/**
 * In-page hex colour picker (react-colorful). The swatch opens a popover with a
 * saturation/hue picker rendered ENTIRELY in the DOM — it never invokes the
 * native OS colour dialog (that native handoff was what hung the UI). You can
 * also just type/paste a hex. Used by both the series and product dialogs.
 */
function ColorField({ label, value, onChange, helperText, trailing }: {
  label: string; value: string; onChange: (hex: string) => void; helperText?: string;
  /** Rendered inline to the right of the hex box — keeps swatch, hex and the
   *  artwork lookup on ONE row so the dialog fits more vertically (Jack, 09/09). */
  trailing?: React.ReactNode;
}) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const safe = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(value) ? value : '#cccccc';
  return (
    <Box>
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Box onClick={(e) => setAnchor(e.currentTarget)} title="Pick a colour"
          sx={{
            width: 34, height: 34, borderRadius: '6px', flexShrink: 0, cursor: 'pointer',
            bgcolor: value || '#ccc', border: '1px solid rgba(0,0,0,0.25)',
          }} />
        <TextField label={label} value={value ?? ''} size="small" fullWidth placeholder="#RRGGBB"
          onChange={(e) => onChange(upHex(e.target.value))} helperText={helperText} />
        {trailing}
      </Stack>
      <Popover open={Boolean(anchor)} anchorEl={anchor} onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
        <Box sx={{ p: 1.5, '& .react-colorful': { width: 210, height: 170 } }}>
          <HexColorPicker color={safe} onChange={(hex) => onChange(upHex(hex))} />
        </Box>
      </Popover>
    </Box>
  );
}

/**
 * Button-triggered artwork colour lookup. Only queries when "Search" is clicked
 * (or Enter) — NEVER per keystroke. Scans extracted artwork for a colour name
 * and lists the real separation hexes to click, so an ink gets an accurate
 * colour without an eyedropper.
 */
function ArtworkColourLookup({ defaultQuery, onPick }: {
  defaultQuery: string; onPick: (hex: string) => void;
}) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [term, setTerm] = useState(defaultQuery);
  const [submitted, setSubmitted] = useState<string | null>(null);

  const q = useQuery({
    queryKey: productionKeys.artworkColours(submitted ?? ''),
    queryFn: () => getArtworkColours(submitted ?? ''),
    enabled: submitted !== null && submitted.trim().length >= 2,
    staleTime: 5 * 60 * 1000,
  });

  return (
    <>
      <Button size="small" variant="outlined" startIcon={<SearchRounded />}
        onClick={(e) => { setTerm(defaultQuery || term); setAnchor(e.currentTarget); }}
        sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
        From artwork
      </Button>
      <Popover open={Boolean(anchor)} anchorEl={anchor} onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
        <Box sx={{ p: 1.5, width: 340 }}>
          <Stack direction="row" spacing={1}>
            <TextField size="small" fullWidth label="Colour name in artwork" value={term}
              onChange={(e) => setTerm(e.target.value.toUpperCase())}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); setSubmitted(term.trim()); } }} />
            <Button variant="contained" onClick={() => setSubmitted(term.trim())}
              sx={{ bgcolor: TOKENS.accent, '&:hover': { bgcolor: TOKENS.accentStrong } }}>Search</Button>
          </Stack>
          <Box sx={{ mt: 1.5, minHeight: 44 }}>
            {q.isFetching && <Stack alignItems="center" sx={{ py: 1 }}><CircularProgress size={20} /></Stack>}
            {!q.isFetching && submitted !== null && (q.data?.length ?? 0) === 0 && (
              <Typography sx={{ fontSize: 12, color: TOKENS.ink3 }}>
                No matching colours found in extracted artwork.
              </Typography>
            )}
            {!q.isFetching && (q.data?.length ?? 0) > 0 && (
              <Stack spacing={0.5}>
                {q.data!.map((c) => (
                  <Stack key={`${c.colour_name}-${c.hex_colour}`} direction="row" alignItems="center" spacing={1}
                    onClick={() => { onPick(c.hex_colour.toUpperCase()); setAnchor(null); }}
                    sx={{ cursor: 'pointer', px: 0.5, py: 0.4, borderRadius: '5px', '&:hover': { bgcolor: TOKENS.lineSoft } }}>
                    <Box sx={{ width: 20, height: 20, borderRadius: '4px', bgcolor: c.hex_colour, border: '1px solid rgba(0,0,0,0.25)', flexShrink: 0 }} />
                    <Typography sx={{ fontSize: 12, fontWeight: 600, flexGrow: 1 }}>{c.colour_name}</Typography>
                    <Typography sx={{ fontFamily: TOKENS.monoFont, fontSize: 11, color: TOKENS.ink3 }}>{c.hex_colour}</Typography>
                    <Typography sx={{ fontSize: 10, color: TOKENS.ink3 }}>{c.uses}×</Typography>
                  </Stack>
                ))}
              </Stack>
            )}
          </Box>
        </Box>
      </Popover>
    </>
  );
}

/** New-product intent: which kind of series to default the dialog to. */
type NewProduct = { prefer: 'ink' | 'coating' };
const isEdit = (d: InkProduct | NewProduct): d is InkProduct => 'ink_key' in d;

/**
 * InkCatalogueView — Materials-maintained ink catalogue
 * ([[ink-mis-integration]]). Two levels: the SERIES master (manufacturer ×
 * series × system → the key suffix) and the PRODUCT register (colour × series ×
 * LMQ → the unique ink_key). Coatings are first-class series here too — coating
 * series (COCO/COUV/COLMQ) key their products by supplier article number.
 * Click a series row to filter the product register beneath it.
 */
/** `embedded` drops the page header — the Ink shell (InkMaterialsView) owns it. */
export default function InkCatalogueView({ embedded }: { embedded?: boolean } = {}) {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [selectedSeries, setSelectedSeries] = useState<string | null>(null);
  const [seriesDialog, setSeriesDialog] = useState<InkSeries | 'new' | null>(null);
  const [productDialog, setProductDialog] = useState<InkProduct | NewProduct | null>(null);

  const seriesQ = useQuery({
    queryKey: productionKeys.inkSeries(showInactive),
    queryFn: () => listInkSeries(showInactive),
    staleTime: 60 * 1000,
  });
  // One register for the whole catalogue — inks AND coatings (material_class 'all').
  const productsQ = useQuery({
    queryKey: productionKeys.inkProducts({ includeInactive: showInactive, search, series: selectedSeries ?? undefined, materialClass: 'all' }),
    queryFn: () => listInkProducts({ includeInactive: showInactive, search, series: selectedSeries ?? undefined, materialClass: 'all' }),
    staleTime: 60 * 1000,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['production', 'ink-series'] });
    qc.invalidateQueries({ queryKey: ['production', 'ink-products'] });
    qc.invalidateQueries({ queryKey: ['production', 'ink-coatings'] });   // coater dropdown (InkAssignmentView)
  };

  const delSeries = useMutation({ mutationFn: deleteInkSeries, onSettled: invalidate });
  const delProduct = useMutation({ mutationFn: deleteInkProduct, onSettled: invalidate });

  const activeSeries = useMemo(() => (seriesQ.data ?? []).filter((s) => s.active), [seriesQ.data]);
  const hasInkSeries = activeSeries.some((s) => !isCoatingSeries(s));
  const hasCoatingSeries = activeSeries.some((s) => isCoatingSeries(s));
  const selected = (seriesQ.data ?? []).find((s) => s.series_suffix === selectedSeries) ?? null;

  const toggleSeries = (suffix: string) =>
    setSelectedSeries((cur) => (cur === suffix ? null : suffix));

  return (
    <Box sx={embedded ? { p: 0 } : { p: { xs: 1.5, md: 2.5 }, maxWidth: 1400, mx: 'auto' }}>
      {!embedded && (
        <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 0.5 }}>
          <MenuBookRounded sx={{ color: TOKENS.accent }} />
          <Typography sx={{ fontWeight: 700, fontSize: 20 }}>Ink catalogue</Typography>
        </Stack>
      )}
      <Typography sx={{ fontSize: 13, color: TOKENS.ink3, mb: 2 }}>
        Materials-maintained. Series define the key suffix; products are colour × series = the unique ink key.
        Coating series key their products by supplier article number. Click a series to filter the register below.
      </Typography>

      {/* ---------- SERIES ---------- */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 15 }}>Series</Typography>
        <Button size="small" startIcon={<AddRounded />} onClick={() => setSeriesDialog('new')}
          sx={{ color: TOKENS.accent }}>Add series</Button>
      </Stack>
      {seriesQ.isLoading ? <Loading /> : seriesQ.isError ? (
        <Alert severity="error">Couldn’t load series.</Alert>
      ) : (
        <Box sx={{ border: '1px solid rgba(0,0,0,0.08)', borderRadius: '10px', overflow: 'hidden', mb: 3 }}>
          <Table size="small" sx={{ '& td, & th': { borderColor: 'rgba(0,0,0,0.06)' } }}>
            <TableHead>
              <TableRow>
                {['Suffix', 'Supplier', 'Series', 'System', 'Colour code', 'Products', ''].map((h) => (
                  <TableCell key={h} sx={{ fontWeight: 700, fontSize: 11.5, color: TOKENS.ink3 }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {(seriesQ.data ?? []).map((s) => {
                const isSel = s.series_suffix === selectedSeries;
                return (
                  <TableRow key={s.series_suffix} hover onClick={() => toggleSeries(s.series_suffix)}
                    sx={{
                      opacity: s.active ? 1 : 0.5, cursor: 'pointer',
                      bgcolor: isSel ? `${TOKENS.accent}14` : undefined,
                      '& > td': isSel ? { borderTop: `1px solid ${TOKENS.accent}55`, borderBottom: `1px solid ${TOKENS.accent}55` } : undefined,
                    }}>
                    <TableCell sx={{ fontFamily: TOKENS.monoFont, fontWeight: 700, color: isSel ? TOKENS.accent : 'inherit' }}>{s.series_suffix}</TableCell>
                    <TableCell sx={{ fontFamily: TOKENS.monoFont, fontWeight: 600 }}>
                      {(s.supplier_code ?? s.manufacturer)?.toUpperCase()}
                    </TableCell>
                    <TableCell sx={{ color: s.low_migration ? LMQ_RED : 'inherit', fontWeight: s.low_migration ? 700 : 400 }}>
                      {s.series_name?.toUpperCase()}
                      {s.low_migration && (
                        <Chip size="small" label="LM" sx={{ ml: 0.5, height: 16, fontSize: 9.5, fontWeight: 800, color: '#fff', bgcolor: LMQ_RED }} />
                      )}
                      {isCoatingSeries(s) && (
                        <Chip size="small" label="COATING" sx={{ ml: 0.5, height: 16, fontSize: 9, fontWeight: 700, color: TOKENS.ink2, bgcolor: `${COATING_HEX}33` }} />
                      )}
                    </TableCell>
                    <TableCell>{s.ink_system}</TableCell>
                    <TableCell>
                      <SeriesTag coating={isCoatingSeries(s)}
                        series={{ manufacturer: s.manufacturer, series_name: s.series_name, ink_system: s.ink_system, series_hex: s.display_hex }} />
                    </TableCell>
                    <TableCell>{s.product_count ?? 0}</TableCell>
                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                      <IconButton size="small" onClick={() => setSeriesDialog(s)}><EditRounded fontSize="small" /></IconButton>
                      <IconButton size="small" onClick={() => {
                        if (window.confirm(`Remove series ${s.series_suffix}? (Deactivated if products reference it.)`)) delSeries.mutate(s.series_suffix);
                      }}><DeleteOutlineRounded fontSize="small" /></IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
              {(seriesQ.data ?? []).length === 0 && (
                <TableRow><TableCell colSpan={7} sx={{ color: TOKENS.ink3, textAlign: 'center', py: 3 }}>
                  No series yet — add one to begin.
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      )}

      {/* ---------- PRODUCTS ---------- */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1, flexWrap: 'wrap', gap: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography sx={{ fontWeight: 700, fontSize: 15 }}>Product register</Typography>
          {selected && (
            <Chip size="small" onDelete={() => setSelectedSeries(null)} deleteIcon={<CloseRounded />}
              label={`${selected.manufacturer} ${selected.series_name} (${selected.series_suffix})`.toUpperCase()}
              sx={{ height: 22, fontSize: 11.5, fontWeight: 700, bgcolor: `${TOKENS.accent}18`, color: TOKENS.accentStrong }} />
          )}
        </Stack>
        <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap">
          <TextField size="small" placeholder="Search key / description / colour…"
            value={search} onChange={(e) => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchRounded fontSize="small" /></InputAdornment> }}
            sx={{ width: 260 }} />
          <FormControlLabel
            control={<Switch size="small" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />}
            label={<Typography sx={{ fontSize: 12.5, color: TOKENS.ink2 }}>Show inactive</Typography>} />
          <Button size="small" variant="outlined" startIcon={<AddRounded />}
            onClick={() => setProductDialog({ prefer: 'coating' })} disabled={!hasCoatingSeries}
            sx={{ color: TOKENS.accent, borderColor: `${TOKENS.accent}80` }}>Add coating</Button>
          <Button size="small" variant="contained" startIcon={<AddRounded />}
            onClick={() => setProductDialog({ prefer: 'ink' })} disabled={!hasInkSeries}
            sx={{ bgcolor: TOKENS.accent, '&:hover': { bgcolor: TOKENS.accentStrong } }}>Add ink</Button>
        </Stack>
      </Stack>

      {productsQ.isLoading ? <Loading /> : productsQ.isError ? (
        <Alert severity="error">Couldn’t load products.</Alert>
      ) : (
        <Box sx={{ border: '1px solid rgba(0,0,0,0.08)', borderRadius: '10px', overflow: 'hidden' }}>
          <Table size="small" sx={{ '& td, & th': { borderColor: 'rgba(0,0,0,0.06)' } }}>
            <TableHead>
              <TableRow>
                {['Key', 'Description', 'Colour', 'Series', 'LM', ''].map((h) => (
                  <TableCell key={h} sx={{ fontWeight: 700, fontSize: 11.5, color: TOKENS.ink3 }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {(productsQ.data ?? []).map((p) => {
                const coating = p.material_class === 'coating';
                return (
                  <TableRow key={p.ink_key} hover sx={{ opacity: p.active ? 1 : 0.5 }}>
                    <TableCell sx={{ fontFamily: TOKENS.monoFont, fontWeight: 700, color: p.low_migration ? LMQ_RED : TOKENS.ink }}>
                      {p.ink_key}
                    </TableCell>
                    <TableCell sx={{ color: p.low_migration ? LMQ_RED : TOKENS.ink2, fontWeight: p.low_migration ? 700 : 400 }}>
                      {p.description?.toUpperCase()}
                    </TableCell>
                    <TableCell>
                      {coating ? (
                        <Stack direction="row" alignItems="center" spacing={0.75}>
                          <Box sx={{ width: 14, height: 14, borderRadius: '3px', bgcolor: p.hex_colour ?? COATING_HEX, border: '1px solid rgba(0,0,0,0.2)' }} />
                          <Typography sx={{ fontSize: 11.5, color: TOKENS.ink3 }}>VARNISH</Typography>
                        </Stack>
                      ) : (
                        <Stack direction="row" alignItems="center" spacing={0.75}>
                          <Box sx={{ width: 14, height: 14, borderRadius: '3px', bgcolor: p.hex_colour ?? '#ccc', border: '1px solid rgba(0,0,0,0.2)' }} />
                          <Typography sx={{ fontSize: 12.5 }}>{p.colour_name?.toUpperCase()}</Typography>
                          <Typography sx={{ fontFamily: TOKENS.monoFont, fontSize: 11, color: TOKENS.ink3 }}>{p.colour_code}</Typography>
                        </Stack>
                      )}
                    </TableCell>
                    <TableCell><SeriesTag series={p} coating={coating} /></TableCell>
                    <TableCell>
                      {p.low_migration && <Chip size="small" label="LMQ" sx={{ height: 18, fontSize: 10.5, fontWeight: 800, color: '#fff', bgcolor: LMQ_RED }} />}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => setProductDialog(p)}><EditRounded fontSize="small" /></IconButton>
                      <IconButton size="small" onClick={() => {
                        if (window.confirm(`Remove ${p.ink_key}? (Deactivated if assigned to a SKU.)`)) delProduct.mutate(p.ink_key);
                      }}><DeleteOutlineRounded fontSize="small" /></IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
              {(productsQ.data ?? []).length === 0 && (
                <TableRow><TableCell colSpan={6} sx={{ color: TOKENS.ink3, textAlign: 'center', py: 3 }}>
                  {activeSeries.length === 0
                    ? 'Add a series first, then add products.'
                    : selected ? `No products in ${selected.series_suffix}.` : 'No products match — add one.'}
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      )}

      {seriesDialog && (
        <SeriesDialog initial={seriesDialog === 'new' ? null : seriesDialog}
          onClose={() => setSeriesDialog(null)} onSaved={() => { setSeriesDialog(null); invalidate(); }} />
      )}
      {productDialog && (
        <ProductDialog
          initial={isEdit(productDialog) ? productDialog : null}
          prefer={isEdit(productDialog) ? (productDialog.material_class === 'coating' ? 'coating' : 'ink') : productDialog.prefer}
          series={activeSeries} preselect={selectedSeries}
          onClose={() => setProductDialog(null)} onSaved={() => { setProductDialog(null); invalidate(); }} />
      )}
    </Box>
  );
}

function Loading() {
  return <Stack alignItems="center" sx={{ py: 4 }}><CircularProgress size={24} /></Stack>;
}

// ---------------------------------------------------------------------------

/**
 * The series key, assembling.
 *
 * Four segments in a row, each labelled with where it came from, and the
 * finished key underneath. The two that are judgement (the supplier prefix and
 * the series discriminator) are editable IN PLACE — everything else is driven
 * by the controls above and is read-only here, so there is no way to produce a
 * key you cannot account for.
 *
 * The collision warning is the point of the whole thing: Jack chose P for
 * NOVASPOT and E for NOVASENS by hand precisely because the obvious first
 * letter would have made both FNCO. This says so before the save instead of
 * after.
 */
/** ALL CAPS, letters and digits only — the house syntax for a key segment. */
const upHex0 = (v: string) => (v || '').toUpperCase().replace(/[^A-Z0-9]/g, '');

function SeriesKeyBuilder({
  editing, supplierCode, seriesName, system, lowMigration,
  prefix, letter, onPrefix, onLetter, composed, clash,
}: {
  editing: boolean;
  supplierCode: string; seriesName: string; system: string; lowMigration: boolean;
  prefix: string; letter: string;
  onPrefix: (v: string) => void; onLetter: (v: string) => void;
  composed: string; clash: boolean;
}) {
  // Read-only segments: flat, no affordance — they follow the fields above.
  const seg = (filled: boolean) => ({
    px: 1, py: 0.5, borderRadius: '7px', textAlign: 'center' as const, minWidth: 46,
    bgcolor: filled ? `${TOKENS.accent}14` : TOKENS.lineSoft,
    border: `1px solid ${filled ? `${TOKENS.accent}55` : TOKENS.line}`,
    transition: 'background-color .15s, border-color .15s',
  });
  // EDITABLE segments: white ground, a solid input border, a pencil, and a
  // focus ring. Jack, 14/09 — "it's not initially clear that the Series Key
  // being built is editable". They were styled identically to the read-only
  // ones, so nothing said "type here".
  const editSeg = (filled: boolean) => ({
    px: 1, pt: 0.5, pb: 0.25, borderRadius: '7px', textAlign: 'center' as const, minWidth: 52,
    bgcolor: TOKENS.panel,
    border: `1.5px solid ${filled ? TOKENS.accent : TOKENS.ink3}`,
    cursor: 'text',
    transition: 'border-color .15s, box-shadow .15s',
    '&:hover': { borderColor: TOKENS.accentStrong },
    '&:focus-within': { borderColor: TOKENS.accent, boxShadow: `0 0 0 3px ${TOKENS.accent}22` },
  });
  const cap = {
    fontSize: 8.5, fontWeight: 800, letterSpacing: 0.4, textTransform: 'uppercase' as const,
    color: TOKENS.ink3, lineHeight: 1.5,
  };
  const val = { fontFamily: TOKENS.monoFont, fontWeight: 800, fontSize: 15, lineHeight: 1.2 };

  return (
    <Box sx={{
      p: 1.5, borderRadius: '10px',
      border: `1px solid ${clash ? TOKENS.bad : TOKENS.line}`,
      bgcolor: clash ? `${TOKENS.bad}0A` : TOKENS.lineSoft,
    }}>
      <Stack direction="row" alignItems="baseline" spacing={0.75} sx={{ mb: 0.75 }}>
        <Typography sx={cap}>Series key</Typography>
        {!editing && (
          <Typography sx={{ fontSize: 10, color: TOKENS.accentStrong, fontWeight: 700 }}>
            — the two boxed fields are yours to change
          </Typography>
        )}
      </Stack>

      {editing ? (
        <Typography sx={{ ...val, fontSize: 22 }}>{composed}</Typography>
      ) : (
        <>
          <Stack direction="row" spacing={0.75} alignItems="flex-start">
            <Box sx={editSeg(!!prefix)}>
              <Typography sx={{ ...cap, color: TOKENS.accentStrong }}>
                Supplier <EditRounded sx={{ fontSize: 8.5, verticalAlign: '-1px' }} />
              </Typography>
              <InputBase value={prefix} onChange={(e: React.ChangeEvent<HTMLInputElement>) => onPrefix(upHex0(e.target.value).slice(0, 3))}
                inputProps={{ 'aria-label': 'supplier prefix', style: { textAlign: 'center', padding: 0 } }}
                sx={{ ...val, width: 38 }} />
            </Box>
            <Box sx={editSeg(!!letter)}>
              <Typography sx={{ ...cap, color: TOKENS.accentStrong }}>
                Series <EditRounded sx={{ fontSize: 8.5, verticalAlign: '-1px' }} />
              </Typography>
              <InputBase value={letter} onChange={(e: React.ChangeEvent<HTMLInputElement>) => onLetter(upHex0(e.target.value).slice(0, 3))}
                inputProps={{ 'aria-label': 'series letter', style: { textAlign: 'center', padding: 0 } }}
                sx={{ ...val, width: 38 }} />
            </Box>
            <Box sx={seg(!!system)}>
              <Typography sx={cap}>System</Typography>
              <Typography sx={val}>{system || '—'}</Typography>
            </Box>
            <Box sx={{ ...seg(lowMigration), opacity: lowMigration ? 1 : 0.4 }}>
              <Typography sx={cap}>Migration</Typography>
              <Typography sx={{ ...val, color: lowMigration ? LMQ_RED : TOKENS.ink3 }}>
                {lowMigration ? 'LMQ' : '—'}
              </Typography>
            </Box>
            <Box sx={{ flex: 1, minWidth: 0, textAlign: 'right', pt: 0.5 }}>
              <Typography sx={cap}>Will create</Typography>
              <Typography sx={{
                ...val, fontSize: 20,
                color: clash ? TOKENS.bad : composed ? TOKENS.accentStrong : TOKENS.ink3,
              }}>
                {composed || '—'}
              </Typography>
            </Box>
          </Stack>

          <Typography sx={{ fontSize: 11, color: TOKENS.ink3, mt: 0.75, lineHeight: 1.45 }}>
            {[supplierCode || 'supplier', seriesName || 'series', systemLabel(system),
              lowMigration ? 'low migration' : null].filter(Boolean).join(' · ')}
            {' — '}system and migration follow the fields above.
          </Typography>

          {clash && (
            <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: TOKENS.bad, mt: 0.75, lineHeight: 1.45 }}>
              {composed} already exists. Change the series letter — that is exactly why NOVASPOT
              is P and NOVASENS is E: both would otherwise have been N, and both keys FNCO.
            </Typography>
          )}
        </>
      )}
    </Box>
  );
}

/** `supplier_code` rides on the series row (migration 109) but the shared
 * upsert type predates it — widen locally rather than touch productionApi.ts. */
type SeriesForm = InkSeriesUpsert & { supplier_code: string };

function SeriesDialog({ initial, onClose, onSaved }: {
  initial: InkSeries | null; onClose: () => void; onSaved: () => void;
}) {
  const editing = !!initial;
  // The two judgement segments of the key. Seeded from an existing series by
  // splitting nothing — an edit keeps its stored suffix verbatim and locks it,
  // because ink_key is composed from it and ink_sku_separation.ink_code holds a
  // plain-TEXT copy with no FK (CLAUDE.md): a rename leaves dangling pointers.
  const [prefix, setPrefix] = useState('');
  const [letter, setLetter] = useState('');
  const [prefixTouched, setPrefixTouched] = useState(false);
  const [letterTouched, setLetterTouched] = useState(false);
  const [f, setF] = useState<SeriesForm>({
    series_suffix: initial?.series_suffix ?? '',
    // The dialog picks a SUPPLIER; `manufacturer` (what every display string
    // composes from) is sent as the same code — see the save below.
    supplier_code: initial?.supplier_code ?? '',
    manufacturer: initial?.manufacturer ?? '',
    series_name: initial?.series_name ?? '',
    ink_system: initial?.ink_system ?? 'CO',
    display_hex: initial?.display_hex ?? '#1976D2',
    low_migration: initial?.low_migration ?? false,
    material_class: initial?.material_class ?? 'ink',
    active: initial?.active ?? true,
    notes: initial?.notes ?? '',
  });
  const suppliersQ = useQuery({
    queryKey: productionKeys.inkSuppliers(false),
    queryFn: () => listInkSuppliers(false),
    staleTime: 60 * 1000,
  });
  const suppliers = suppliersQ.data ?? [];
  // Every series, active or not — a key collides with an inactive series just
  // as hard as with a live one.
  const allSeriesQ = useQuery({
    queryKey: productionKeys.inkSeries(true),
    queryFn: () => listInkSeries(true),
    staleTime: 60 * 1000,
  });
  const save = useMutation({ mutationFn: (p: SeriesForm) => upsertInkSeries(p), onSuccess: onSaved });
  const coating = f.material_class === 'coating';

  // Suggestions follow the inputs until the user overrides one, then they stop
  // moving underneath them.
  useEffect(() => {
    if (!editing && !prefixTouched) setPrefix(suggestPrefix(f.supplier_code));
  }, [f.supplier_code, prefixTouched, editing]);
  useEffect(() => {
    if (!editing && !letterTouched) setLetter(suggestLetter(f.series_name));
  }, [f.series_name, letterTouched, editing]);

  const composed = composeSeriesKey({
    prefix, letter, system: f.ink_system, lowMigration: !!f.low_migration,
  });
  // An edit never recomposes — it shows what is stored.
  const seriesKey = editing ? (initial!.series_suffix) : composed;
  const clash = !editing && !!seriesKey
    && (allSeriesQ.data ?? []).some((x) => x.series_suffix.toUpperCase() === seriesKey);

  const valid = !!seriesKey && !clash && !!f.supplier_code
    && !!f.series_name.trim() && !!f.ink_system.trim();

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>{editing ? `Edit series ${initial!.series_suffix}` : 'Add series'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <FormControlLabel
            control={<Switch checked={coating}
              onChange={(e) => setF({ ...f, material_class: e.target.checked ? 'coating' : 'ink', display_hex: e.target.checked ? COATING_HEX : f.display_hex })} />}
            label={<Typography sx={{ fontSize: 13, fontWeight: 700, color: coating ? TOKENS.ink2 : TOKENS.ink3 }}>
              Coating series (products keyed by article number)</Typography>} />

          {/* 1 — supplier. First, because it is the first segment of the key. */}
          <FormControl size="small" fullWidth>
            <InputLabel>Supplier</InputLabel>
            <Select label="Supplier" value={f.supplier_code}
              onChange={(e) => setF({ ...f, supplier_code: String(e.target.value) })}
              // Same reason as System: the closed control would otherwise run
              // the code straight into the name ("FLINTFLINT" when they match).
              renderValue={(v) => {
                const sup = suppliers.find((x) => x.supplier_code === v);
                const name = sup?.name?.toUpperCase();
                return (
                  <Box component="span">
                    <Box component="span" sx={{ fontFamily: TOKENS.monoFont, fontWeight: 700 }}>{String(v)}</Box>
                    {name && name !== String(v) && (
                      <Box component="span" sx={{ color: TOKENS.ink3 }}>{` — ${name}`}</Box>
                    )}
                  </Box>
                );
              }}>
              {suppliers.map((sup) => (
                <MenuItem key={sup.supplier_code} value={sup.supplier_code}>
                  <Box component="span" sx={{ fontFamily: TOKENS.monoFont, fontWeight: 700, minWidth: 54 }}>{sup.supplier_code}</Box>
                  <Box component="span" sx={{ color: TOKENS.ink3 }}>{sup.name?.toUpperCase()}</Box>
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              {suppliers.length === 0 && !suppliersQ.isLoading
                ? 'No suppliers yet — add one under Ink stock › Suppliers first.'
                : 'Every ink in this series inherits this supplier.'}
            </FormHelperText>
          </FormControl>

          {/* 2 — the series name. */}
          <TextField label="Series name" value={f.series_name}
            onChange={(e) => setF({ ...f, series_name: up(e.target.value) })} size="small" fullWidth
            helperText="The manufacturer's own range name, e.g. NOVASENS. Leave LMQ off — the tick below adds it." />

          {/* 3 — low migration. Ticking it appends LMQ to the key. */}
          <FormControlLabel
            control={<Checkbox checked={!!f.low_migration}
              onChange={(e) => setF({ ...f, low_migration: e.target.checked })} />}
            label={<Typography sx={{ fontSize: 13, fontWeight: 700, color: f.low_migration ? LMQ_RED : TOKENS.ink2 }}>
              Low migration — adds <Box component="span" sx={{ fontFamily: TOKENS.monoFont }}>LMQ</Box> to the key
            </Typography>} />

          {/* 4 — the system, spelled out. */}
          <FormControl size="small" fullWidth>
            <InputLabel>System</InputLabel>
            <Select label="System" value={f.ink_system}
              onChange={(e) => setF({ ...f, ink_system: String(e.target.value) })}
              // Without an explicit renderValue MUI reproduces the MenuItem's
              // children in the closed control, and the code runs straight into
              // the label — "COCONVENTIONAL".
              renderValue={(v) => (
                <Box component="span">
                  <Box component="span" sx={{ fontFamily: TOKENS.monoFont, fontWeight: 700 }}>{String(v)}</Box>
                  <Box component="span" sx={{ color: TOKENS.ink3 }}>{` — ${systemLabel(String(v))}`}</Box>
                </Box>
              )}>
              {SYSTEMS.map((sys) => (
                <MenuItem key={sys.code} value={sys.code}>
                  <Box component="span" sx={{ fontFamily: TOKENS.monoFont, fontWeight: 700, minWidth: 30 }}>{sys.code}</Box>
                  <Box component="span" sx={{ color: TOKENS.ink3 }}>{sys.label.toUpperCase()}</Box>
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>Never mixed: stock and demand are never summed across systems.</FormHelperText>
          </FormControl>

          {/* 5 — the key, assembling. The two editable segments sit IN the
              preview rather than above it, so the key visibly builds itself as
              the fields above are filled in (Jack, 11/09 — "additive"). */}
          <SeriesKeyBuilder
            editing={editing}
            supplierCode={f.supplier_code}
            seriesName={f.series_name}
            system={f.ink_system}
            lowMigration={!!f.low_migration}
            prefix={prefix} letter={letter}
            onPrefix={(v) => { setPrefixTouched(true); setPrefix(v); }}
            onLetter={(v) => { setLetterTouched(true); setLetter(v); }}
            composed={seriesKey}
            clash={clash}
          />

          <ColorField label="Series colour (hex)" value={f.display_hex ?? ''}
            onChange={(hex) => setF({ ...f, display_hex: hex })}
            helperText="Used to colour-code this series everywhere (grid, sticker, labels)." />
          {editing && (
            <FormControlLabel control={<Checkbox checked={f.active ?? true}
              onChange={(e) => setF({ ...f, active: e.target.checked })} />} label="Active" />
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!valid || save.isPending}
          onClick={() => save.mutate({
            ...f,
            series_suffix: seriesKey,
            // Supplier IS the manufacturer here; the backend falls back to the
            // supplier code anyway when a caller sends no manufacturer.
            manufacturer: f.supplier_code,
            display_hex: upHex(f.display_hex) || null,
          })}
          sx={{ bgcolor: TOKENS.accent, '&:hover': { bgcolor: TOKENS.accentStrong } }}>Save</Button>
      </DialogActions>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------

/** Section heading inside the product dialog — identity / commercial. */
function GroupLabel({ children }: { children: React.ReactNode }) {
  return (
    <Typography sx={{ fontSize: 10.5, textTransform: 'uppercase', letterSpacing: 0.4,
      color: TOKENS.ink3, fontWeight: 700 }}>
      {children}
    </Typography>
  );
}

/**
 * ProductDialog — add/edit a product. Adapts to the chosen series: an INK series
 * composes the key colour×series and takes a CMYK swatch; a COATING series keys
 * the product by its supplier article number and names it directly (varnish
 * tint, no colour kind). One dialog for the whole register.
 *
 * Two groups: IDENTITY (series, code, kind, name, hex — what composes the key)
 * and COMMERCIAL (pack, cost, article no, reorder point — what ordering runs
 * on). The supplier is not a field: a product inherits its series' supplier.
 */
function ProductDialog({ initial, prefer, series, preselect, onClose, onSaved }: {
  initial: InkProduct | null; prefer: 'ink' | 'coating'; series: InkSeries[];
  preselect: string | null; onClose: () => void; onSaved: () => void;
}) {
  const editing = !!initial;
  // Default series for a NEW product: the highlighted series if it matches the
  // requested kind, else the first series of that kind, else anything.
  const wantCoating = prefer === 'coating';
  const preHit = series.find((s) => s.series_suffix === preselect && isCoatingSeries(s) === wantCoating);
  const kindFirst = series.find((s) => isCoatingSeries(s) === wantCoating);
  const defaultSuffix = initial?.series_suffix ?? preHit?.series_suffix ?? kindFirst?.series_suffix ?? series[0]?.series_suffix ?? '';

  const [f, setF] = useState({
    colour_code: initial?.colour_code ?? '',
    colour_name: initial?.colour_name ?? '',
    colour_kind: initial?.colour_kind ?? 'process',
    series_suffix: defaultSuffix,
    hex_colour: upHex(initial?.hex_colour) || (wantCoating ? COATING_HEX : '#00A0E0'),
    active: initial?.active ?? true,
    notes: initial?.notes ?? '',
    // Commercial (migration 109). Kept as strings so the fields can be left
    // blank — blank means "not set", which is NOT the same as zero. A zero
    // reorder point would mean "never reorder"; null means "no point set yet".
    // Prefilled to 1 dp — the display convention across the ink side.
    supplier_article_no: initial?.supplier_article_no ?? '',
    pack_size_kg: dp1(initial?.pack_size_kg),
    standard_cost_per_kg: asIs(initial?.standard_cost_per_kg),
    reorder_point_kg: dp1(initial?.reorder_point_kg),
  });
  const save = useMutation({ mutationFn: (p: InkProductUpsert) => upsertInkProduct(p), onSuccess: onSaved });

  const chosen = series.find((s) => s.series_suffix === f.series_suffix);
  const coating = isCoatingSeries(chosen);
  const isLM = !!chosen?.low_migration;           // LM is inherited from the series
  const preview = composeInk(chosen, f.colour_code, f.colour_name);
  const valid = f.colour_code.trim() && f.colour_name.trim() && f.series_suffix;
  // Inherited, not chosen — shown so it's obvious who this ink will be bought from.
  const seriesSupplier = chosen?.supplier_code ?? chosen?.manufacturer ?? null;

  const num = (s: string) => (s.trim() === '' ? null : Number(s));

  const title = editing
    ? (coating ? `Edit coating ${initial!.ink_key}` : `Edit ${initial!.ink_key}`)
    : (coating ? 'Add coating' : 'Add ink');

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>{title}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 0.5 }}>

          {/* ---- Identity: what composes the key ---------------------------- */}
          <Stack spacing={2}>
            <GroupLabel>Identity</GroupLabel>
            <FormControl size="small" fullWidth>
              <InputLabel>Series</InputLabel>
              <Select label="Series" value={f.series_suffix} onChange={(e) => setF({ ...f, series_suffix: String(e.target.value) })}>
                {series.map((s) => (
                  <MenuItem key={s.series_suffix} value={s.series_suffix}
                    sx={{ color: s.low_migration ? LMQ_RED : undefined }}>
                    {isCoatingSeries(s)
                      ? `${s.manufacturer} ${s.series_name} (${s.series_suffix})`
                      : `${s.manufacturer} ${s.series_name} ${s.ink_system} (${s.series_suffix})`}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                Supplier: <strong>{seriesSupplier ?? 'none on series'}</strong> — inherited from the series.
              </FormHelperText>
            </FormControl>
            <Stack direction="row" spacing={2}>
              <TextField label={coating ? 'Article no.' : 'Colour code'} value={f.colour_code}
                onChange={(e) => setF({ ...f, colour_code: up(e.target.value) })}
                helperText={coating ? 'Supplier article no. (e.g. W-6230, 4211)' : 'C · M · Y · B (black) · P7680 · SKC'}
                size="small" sx={{ flex: 1 }} />
              {!coating && (
                <FormControl size="small" sx={{ flex: 1 }}>
                  <InputLabel>Colour kind</InputLabel>
                  <Select label="Colour kind" value={f.colour_kind ?? ''} onChange={(e) => setF({ ...f, colour_kind: String(e.target.value) })}>
                    {COLOUR_KINDS.map((k) => <MenuItem key={k} value={k}>{k.toUpperCase()}</MenuItem>)}
                  </Select>
                </FormControl>
              )}
            </Stack>
            <TextField label={coating ? 'Name' : 'Colour name'} value={f.colour_name}
              onChange={(e) => setF({ ...f, colour_name: up(e.target.value) })}
              helperText={coating ? 'e.g. SAPHIRA W3945 GLOSS' : 'CYAN · PMS 7680 · KOMBUCHA CREAM'} size="small" fullWidth />
            {!coating && (
              <ColorField label="Ink colour (hex)" value={f.hex_colour ?? ''}
                onChange={(hex) => setF({ ...f, hex_colour: hex })}
                trailing={(
                  <ArtworkColourLookup defaultQuery={f.colour_name}
                    onPick={(hex) => setF((prev) => ({ ...prev, hex_colour: upHex(hex) }))} />
                )} />
            )}
          </Stack>

          <Divider />

          {/* ---- Commercial: what ordering and reordering run on ------------ */}
          <Stack spacing={2}>
            <GroupLabel>Commercial</GroupLabel>
            <Stack direction="row" spacing={2}>
              <TextField label="Pack size" value={f.pack_size_kg}
                onChange={(e) => setF({ ...f, pack_size_kg: e.target.value })}
                type="number" size="small" sx={{ flex: 1 }}
                inputProps={{ step: 0.1, min: 0 }}
                InputProps={{ endAdornment: <InputAdornment position="end">kg</InputAdornment> }}
                helperText="Pail / tin size" />
              <TextField label="Standard cost" value={f.standard_cost_per_kg}
                onChange={(e) => setF({ ...f, standard_cost_per_kg: e.target.value })}
                type="number" size="small" sx={{ flex: 1 }}
                inputProps={{ step: 0.01, min: 0 }}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  endAdornment: <InputAdornment position="end">/kg</InputAdornment>,
                }}
                helperText="Planning cost" />
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField label="Supplier article no." value={f.supplier_article_no ?? ''}
                onChange={(e) => setF({ ...f, supplier_article_no: up(e.target.value) })}
                helperText="Their code — what goes on the PO" size="small" sx={{ flex: 1 }} />
              <TextField label="Reorder point" value={f.reorder_point_kg}
                onChange={(e) => setF({ ...f, reorder_point_kg: e.target.value })}
                type="number" size="small" sx={{ flex: 1 }}
                inputProps={{ step: 0.1, min: 0 }}
                InputProps={{ endAdornment: <InputAdornment position="end">kg</InputAdornment> }}
                helperText="Flag at or below" />
            </Stack>
          </Stack>

          {editing && (
            <FormControlLabel control={<Checkbox checked={f.active}
              onChange={(e) => setF({ ...f, active: e.target.checked })} />} label="Active" />
          )}

          {/* live preview of the composed key + description (red if LM series) */}
          <Box sx={{ p: 1.5, borderRadius: '8px', bgcolor: TOKENS.lineSoft }}>
            <Typography sx={{ fontSize: 10.5, textTransform: 'uppercase', letterSpacing: 0.4, color: TOKENS.ink3 }}>
              Will create{coating ? ' · coating' : ''}{isLM ? ' · low migration' : ''}
            </Typography>
            <Typography sx={{ fontFamily: TOKENS.monoFont, fontWeight: 800, fontSize: 18, color: isLM ? LMQ_RED : TOKENS.ink }}>
              {preview.key || '—'}
            </Typography>
            <Typography sx={{ fontSize: 12.5, color: isLM ? LMQ_RED : TOKENS.ink2 }}>
              {preview.description || '—'}
            </Typography>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!valid || save.isPending}
          onClick={() => save.mutate({
            colour_code: f.colour_code.trim().toUpperCase(),
            colour_name: f.colour_name.trim().toUpperCase(),
            colour_kind: coating ? null : (f.colour_kind || null),
            series_suffix: f.series_suffix,
            material_class: coating ? 'coating' : 'ink',
            hex_colour: coating ? (upHex(f.hex_colour) || COATING_HEX) : (upHex(f.hex_colour) || null),
            active: f.active,
            notes: f.notes || null,
            // No supplier on the product: null = inherit the series' supplier.
            supplier_code: null,
            supplier_article_no: f.supplier_article_no || null,
            pack_size_kg: num(f.pack_size_kg),
            standard_cost_per_kg: num(f.standard_cost_per_kg),
            reorder_point_kg: num(f.reorder_point_kg),
            original_key: editing ? initial!.ink_key : null,
          })}
          sx={{ bgcolor: TOKENS.accent, '&:hover': { bgcolor: TOKENS.accentStrong } }}>Save</Button>
      </DialogActions>
    </Dialog>
  );
}
